import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { AdminShell } from '../../components/AdminShell';
import { formatDate } from '../../lib/format';

interface AuditEntry {
  id: string;
  workspace_id: string | null;
  company_name: string | null;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  target_type: string;
  target_id: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** Admin → System audit log. Read-only stream across every workspace. */
export function AdminAuditPage() {
  const { t, i18n } = useTranslation();
  const rows = useQuery<AuditEntry[]>({
    queryKey: ['admin-audit'],
    queryFn: async () => (await api.get('/admin/audit')).data,
    refetchInterval: 30_000,
  });

  return (
    <AdminShell>
      <div className="px-8 py-7 max-w-7xl w-full mx-auto">
        <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500">{t('admin.eyebrow')}</div>
        <h1 className="display text-3xl font-semibold tracking-tight mt-1">{t('admin.audit.title')}</h1>
        <p className="text-sm text-ink-600 dark:text-ink-300 mt-1">{t('admin.audit.subtitle')}</p>

        <Card className="mt-6 overflow-hidden">
          {rows.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !rows.data || rows.data.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('admin.audit.empty')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {rows.data.map((r) => (
                <li key={r.id} className="px-5 py-3 grid grid-cols-12 gap-3 items-start text-sm">
                  <div className="col-span-3">
                    <div className="font-medium">{r.actor_email ?? '—'}</div>
                    <div className="text-xs text-ink-500">{r.company_name ?? '—'}</div>
                  </div>
                  <div className="col-span-2">
                    <Pill tone="muted">{r.action}</Pill>
                  </div>
                  <div className="col-span-5 min-w-0">
                    <div className="text-ink-700 dark:text-ink-200 truncate">
                      <span className="text-ink-500">{r.target_type}</span> <span className="font-mono">{r.target_id}</span>
                    </div>
                    {r.metadata ? (
                      <pre className="text-[11px] text-ink-500 truncate font-mono">{JSON.stringify(r.metadata)}</pre>
                    ) : null}
                  </div>
                  <div className="col-span-2 text-right num text-xs text-ink-500">
                    {formatDate(r.created_at, i18n.language)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AdminShell>
  );
}
